'use client';

import HelpCenterFooter from '@/components/HelpCenterFooter';
import { StatusHero } from '@/components/StatusHero';
import {
  getAllArticles,
  getHelpCategories,
} from '@/data/helpCenterContent';
import { createHelpCenterSearch } from '@/utils/helpCenterSearch';
import {
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useMemo, useState } from 'react';

const HELP_ICONS = {
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  PhoneIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  UserCircleIcon,
};

export default function HelpCenter() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const allArticles = useMemo(() => getAllArticles(HELP_ICONS), []);
  const categories = useMemo(() => getHelpCategories(HELP_ICONS), []);
  const searchEngine = useMemo(() => createHelpCenterSearch(allArticles), [allArticles]);

  // Filter articles based on search and category
  const filteredArticles = useMemo(() => {
    return searchEngine.searchAndFilter(searchQuery, selectedCategory);
  }, [searchEngine, searchQuery, selectedCategory]);

  return (
    <>
      <StatusHero 
        icon={QuestionMarkCircleIcon} 
        title="Help Center" 
        description="Find answers to common questions and get the support you need to make the most of your AI receptionist." 
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search help articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Category Filter Pills */}
            <div className="mb-8">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Articles
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.title}
                  </button>
                ))}
              </div>
            </div>

            {/* Results Count */}
            <div className="mb-6">
              <p className="text-gray-600">
                {filteredArticles.length} {filteredArticles.length === 1 ? 'article' : 'articles'} found
                {searchQuery && ` for "${searchQuery}"`}
              </p>
            </div>

            {/* Article Cards Grid */}
            {filteredArticles.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6">
                {filteredArticles.map((article) => {
                  const Icon = article.categoryIcon;
                  return (
                    <Link
                      key={article.slug}
                      href={`/help-center/${article.slug}`}
                      className="block bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-blue-300 transition-all group"
                    >
                      <div className="flex items-start gap-4">
                        <Icon className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                        <div className="flex-1 min-w-0">
                          <div className="mb-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {article.categoryTitle}
                            </span>
                          </div>
                          <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                            {article.title}
                          </h3>
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {article.description}
                          </p>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-12 text-center">
                <QuestionMarkCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No articles found</h3>
                <p className="text-gray-600 mb-4">
                  We couldn't find any articles matching your search.
                </p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('all');
                  }}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Clear filters
                </button>
              </div>
            )}
      </div>

      <HelpCenterFooter />
    </>
  );
}
